import type {
  ActivityDayPoint,
  DashboardOverview,
  EngagementComparison,
  TimeWindow,
  TopPlayer,
} from './types';
import { ROSTER_MOVES_TRACKED_SINCE } from './types';
import {
  completionRate,
  detectExceptionalActivity,
  detectPoolGrowth,
  engagementScore,
  selectNewJoinersKpi,
  selectWindowedKpi,
  windowDays,
} from './metrics';

/**
 * Récit de la vue d'ensemble : transforme les compteurs des RPC `ci_*` en
 * phrases simples pour l'opérateur du pool. Purement descriptif : chaque
 * phrase repose sur une valeur observée, sans projection.
 */

export type TrendDirection = 'up' | 'down' | 'flat' | 'new' | 'none';

export interface Trend {
  direction: TrendDirection;
  /** Variation arrondie en %, null quand la période précédente vaut 0. */
  percent: number | null;
  /** Rapport courant / précédent, null quand la période précédente vaut 0. */
  ratio: number | null;
}

/** Au-delà de cette hausse (en %), on parle en « N fois plus », plus lisible. */
const RATIO_THRESHOLD = 200;

/** Variation sous ce seuil (en %) considérée comme stable. */
const FLAT_THRESHOLD = 5;

export function describeTrend(current: number, previous: number): Trend {
  if (previous <= 0) {
    return { direction: current > 0 ? 'new' : 'none', percent: null, ratio: null };
  }
  const ratio = current / previous;
  const percent = Math.round((ratio - 1) * 100);
  if (Math.abs(percent) < FLAT_THRESHOLD) return { direction: 'flat', percent, ratio };
  return { direction: percent > 0 ? 'up' : 'down', percent, ratio };
}

/** « +11 % », « -21 % » ou « 21 fois plus » pour les fortes hausses. */
function formatChange(trend: Trend): string {
  if (trend.percent === null || trend.ratio === null) return '';
  if (trend.percent >= RATIO_THRESHOLD) {
    const ratio = trend.ratio < 10 ? Math.round(trend.ratio * 10) / 10 : Math.round(trend.ratio);
    return `${formatNumber(ratio)} fois plus`;
  }
  return `${trend.percent > 0 ? '+' : ''}${formatNumber(trend.percent)} %`;
}

export const PERIOD_PHRASES: Record<TimeWindow, { current: string; previous: string }> = {
  '7d': { current: 'ces 7 derniers jours', previous: '7 jours précédents' },
  '30d': { current: 'ces 30 derniers jours', previous: '30 jours précédents' },
  '90d': { current: 'ces 90 derniers jours', previous: '90 jours précédents' },
  season: { current: 'depuis le début de la saison', previous: '' },
};

/** Libellé court d'une tendance pour les tuiles : « ▲ +11 % vs 7 jours précédents ». */
export function trendLabel(trend: Trend, window: TimeWindow): string | null {
  const previous = PERIOD_PHRASES[window].previous;
  if (!previous) return null;
  if (trend.direction === 'none') return null;
  if (trend.direction === 'new') return `Aucun pendant les ${previous}`;
  if (trend.direction === 'flat') return `Stable vs ${previous}`;
  return `${trend.direction === 'up' ? '▲' : '▼'} ${formatChange(trend)} vs ${previous}`;
}

const numberFormat = new Intl.NumberFormat('fr-CA');
const dayFormat = new Intl.DateTimeFormat('fr-CA', { day: 'numeric', month: 'long', timeZone: 'UTC' });

const seasonDayFormat = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'long',
  timeZone: 'America/Toronto',
});

/** Début de saison renvoyé par les RPC (timestamptz) -> « 30 septembre », heure de Montréal. */
export function formatSeasonStart(iso: string): string {
  return seasonDayFormat.format(new Date(iso));
}

/** La période précédente est-elle entièrement dans la saison ? Sinon, pas de comparaison. */
export function isComparable(comparison: EngagementComparison | null): comparison is EngagementComparison {
  return comparison !== null && comparison.previous_complete !== false;
}

export function formatNumber(value: number): string {
  return numberFormat.format(value);
}

/** « 2026-10-07 » -> « 7 octobre ». */
export function formatLongDay(iso: string): string {
  return dayFormat.format(new Date(`${iso}T00:00:00Z`));
}

function countOf(n: number, singular: string, plural: string): string {
  return `${formatNumber(n)} ${n > 1 ? plural : singular}`;
}

function joinList(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}

export function formatPerDay(total: number, days: number): string {
  if (days <= 0) return '0';
  const value = total / days;
  return formatNumber(value < 10 ? Math.round(value * 10) / 10 : Math.round(value));
}

export interface OverviewStoryInput {
  window: TimeWindow;
  overview: DashboardOverview;
  comparison: EngagementComparison | null;
  /** Série quotidienne complète (fenêtre + 7 jours de contexte). */
  history: ActivityDayPoint[];
  topSigned?: TopPlayer | null;
}

export type StoryActionId = 'incomplete-teams' | 'slowdown' | 'growth' | 'injuries';

export interface StoryAction {
  id: StoryActionId;
  text: string;
}

export interface OverviewStory {
  headline: string;
  points: string[];
  actions: StoryAction[];
}

export function buildOverviewStory(input: OverviewStoryInput): OverviewStory {
  const { window, overview, comparison, history, topSigned } = input;
  const period = PERIOD_PHRASES[window];
  const total = overview.total_poolers ?? 0;
  const current = history.slice(-windowDays(window));
  const signings = comparison?.signings_current ?? selectWindowedKpi(overview, 'signings', window);
  // Valeurs courantes de `comparison`, mais tendances seulement si la période précédente est complète.
  const previous = isComparable(comparison) ? comparison : null;
  const currentPhrase =
    window === 'season' && overview.season_start
      ? `depuis le ${formatSeasonStart(overview.season_start)}, début de la saison`
      : period.current;

  if (total === 0) {
    return { headline: 'Ce pool n’a pas encore de poolers inscrits.', points: [], actions: [] };
  }

  // Phrase d'ouverture : la part de poolers actifs et son évolution.
  let headline: string;
  const signerTrend = previous
    ? describeTrend(previous.poolers_signing_current, previous.poolers_signing_previous)
    : null;
  if (comparison && comparison.poolers_signing_current === 0) {
    headline = `Aucun pooler n’a signé de joueur ${currentPhrase}.`;
  } else if (comparison) {
    const pct = engagementScore(comparison.poolers_signing_current, total);
    const base = `${pct} % de vos poolers ont signé au moins un joueur ${currentPhrase}`;
    const suffix =
      signerTrend?.direction === 'up'
        ? `, plus que pendant les ${period.previous}.`
        : signerTrend?.direction === 'down'
          ? `, moins que pendant les ${period.previous}.`
          : signerTrend?.direction === 'flat'
            ? `, autant que pendant les ${period.previous}.`
            : '.';
    headline = base + suffix;
  } else {
    headline =
      signings > 0
        ? `Le pool compte ${countOf(total, 'pooler', 'poolers')}, qui ont fait ${countOf(signings, 'signature d’agent libre', 'signatures d’agents libres')} ${currentPhrase}.`
        : `Le pool compte ${countOf(total, 'pooler', 'poolers')} ; aucune signature d’agent libre ${currentPhrase}.`;
  }

  const points: string[] = [];

  // Sans comparaison, la phrase d'ouverture donne déjà le total de signatures.
  if (signings > 0 && comparison) {
    let sentence = countOf(signings, 'signature d’agent libre', 'signatures d’agents libres');
    if (window !== 'season') {
      sentence += `, soit environ ${formatPerDay(signings, current.length || windowDays(window))} par jour`;
    }
    if (previous) {
      const t = describeTrend(signings, previous.signings_previous);
      if ((t.direction === 'up' || t.direction === 'down') && t.percent !== null) {
        sentence +=
          t.percent >= RATIO_THRESHOLD
            ? ` (${formatChange(t)} que pendant les ${period.previous})`
            : ` (${formatChange(t)} par rapport aux ${period.previous})`;
      } else if (t.direction === 'flat') {
        sentence += ` (stable par rapport aux ${period.previous})`;
      }
    }
    points.push(`${sentence}.`);
  } else if (comparison) {
    points.push(`Aucune signature d’agent libre ${currentPhrase}.`);
  }

  if (current.length >= 3) {
    const peak = current.reduce((best, day) => (day.signings > best.signings ? day : best), current[0]);
    if (peak.signings > 0) {
      points.push(
        `Journée la plus active : le ${formatLongDay(peak.day)}, avec ${countOf(peak.signings, 'signature', 'signatures')}.`
      );
    }
  }

  const exceptional = detectExceptionalActivity(history);
  if (exceptional) {
    points.push(
      `Pic inhabituel : ${countOf(exceptional.current, 'signature', 'signatures')} lors de la dernière journée, au moins deux fois la moyenne des 7 jours précédents.`
    );
  }

  if (topSigned?.player_name && topSigned.event_count > 0) {
    points.push(
      `Joueur le plus signé : ${topSigned.player_name} (${countOf(topSigned.event_count, 'fois', 'fois')}).`
    );
  }

  if (window !== 'season') {
    const joiners = comparison?.new_poolers_current ?? selectNewJoinersKpi(overview, window);
    if (joiners > 0) {
      let sentence = `${countOf(joiners, 'nouveau pooler s’est inscrit', 'nouveaux poolers se sont inscrits')}`;
      if (previous && previous.new_poolers_previous > 0) {
        sentence += `, contre ${formatNumber(previous.new_poolers_previous)} pendant les ${period.previous}`;
      }
      points.push(`${sentence}.`);
    }
  }

  const trades = selectWindowedKpi(overview, 'trades', window);
  if (trades > 0) {
    points.push(`${countOf(trades, 'échange conclu', 'échanges conclus')} entre poolers.`);
  }

  const drops = selectWindowedKpi(overview, 'drops', window);
  const placed = selectWindowedKpi(overview, 'ltir_placed', window);
  const reactivated = selectWindowedKpi(overview, 'ltir_reactivated', window);
  const moves = [
    drops > 0 ? countOf(drops, 'libération', 'libérations') : null,
    placed > 0 ? countOf(placed, 'mise en LTIR', 'mises en LTIR') : null,
    reactivated > 0 ? countOf(reactivated, 'réactivation', 'réactivations') : null,
  ].filter((m): m is string => m !== null);
  if (moves.length > 0) {
    points.push(`Mouvements d’effectif (comptés depuis le ${ROSTER_MOVES_TRACKED_SINCE}) : ${joinList(moves)}.`);
  }

  const incomplete = overview.incomplete_builds ?? 0;
  const rate = completionRate(overview);
  points.push(
    incomplete === 0
      ? 'Toutes les équipes sont complètes.'
      : `${rate} % des équipes sont complètes ; ${countOf(incomplete, 'équipe a', 'équipes ont')} encore des postes à combler.`
  );

  // Pistes d'action : au plus trois, uniquement quand un signal les justifie.
  const actions: StoryAction[] = [];
  if (incomplete > 0 && rate < 90) {
    actions.push({
      id: 'incomplete-teams',
      text: `Relancer ${incomplete > 1 ? `les ${formatNumber(incomplete)} poolers dont l’équipe est incomplète` : 'le pooler dont l’équipe est incomplète'}.`,
    });
  }
  if (signerTrend?.direction === 'down' && (signerTrend.percent ?? 0) <= -20) {
    actions.push({
      id: 'slowdown',
      text: 'L’activité ralentit : un rappel sur les agents libres disponibles pourrait relancer les signatures.',
    });
  }
  if (previous && detectPoolGrowth(previous.new_poolers_current, previous.new_poolers_previous)) {
    actions.push({
      id: 'growth',
      text: 'Les inscriptions accélèrent : un message d’accueil qui explique les règles du pool aiderait les nouveaux.',
    });
  }
  if (placed > 0) {
    actions.push({
      id: 'injuries',
      text: 'Des joueurs ont été placés en LTIR : un contenu sur les blessures du moment pourrait intéresser vos poolers.',
    });
  }

  return { headline, points, actions: actions.slice(0, 3) };
}
